import { axiosAzureInstance } from '../Utils/axios';
import FileDownload from 'js-file-download';
import type { AxiosResponse } from 'axios';

interface ScrapeRequest {
  country: string;
  file: FormData;
}

export const scrapeCountry = async (request: ScrapeRequest): Promise<AxiosResponse> => {
  return axiosAzureInstance.post(
    `/webscraper-node/api/scrape?country=${request.country}`,
    request.file
  );
};

export const getJobStatus = async (jobId: string): Promise<AxiosResponse> => {
  return axiosAzureInstance.get(`/webscraper-node/api/scrape/${jobId}/status`);
};

export const stopJob = async (jobId: string): Promise<AxiosResponse> => {
  return axiosAzureInstance.post(`/webscraper-node/api/scrape/${jobId}/stop`);
};

export const retryJob = async (jobId: string): Promise<AxiosResponse> => {
  return axiosAzureInstance.post(`/webscraper-node/api/scrape/${jobId}/retry`);
};

export const getAvailableCountries = async (): Promise<AxiosResponse> => {
  return axiosAzureInstance.get('/webscraper-node/api/countries');
};

const buildOverviewParams = (
  page: number | string,
  size: number | string,
  sortBy?: string,
  sortOrder?: string,
  search?: string
): URLSearchParams => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (sortBy) params.append('sortBy', sortBy);
  if (sortOrder) params.append('sortOrder', sortOrder);
  const term = typeof search === 'string' ? search.trim() : '';
  if (term) params.append('search', term);
  return params;
};

export const getScrapingResults = async (
  page: number | string,
  size: number | string,
  sortBy?: string,
  sortOrder?: string,
  search?: string
): Promise<AxiosResponse> => {
  const params = buildOverviewParams(page, size, sortBy, sortOrder, search);
  return axiosAzureInstance.get(`/webscraper-node/api/overview?${params.toString()}`);
};

export const getScrapingStatusTotals = async (search?: string): Promise<AxiosResponse> => {
  const params = new URLSearchParams();
  const term = typeof search === 'string' ? search.trim() : '';
  if (term) params.append('search', term);
  const qs = params.toString();
  return axiosAzureInstance.get(`/webscraper-node/api/overview/status-totals${qs ? `?${qs}` : ''}`);
};

export const getAllInProgress = async (
  page: number | string,
  size: number | string,
  sortBy?: string,
  sortOrder?: string,
  search?: string
): Promise<AxiosResponse> => {
  const params = buildOverviewParams(page, size, sortBy, sortOrder, search);
  return axiosAzureInstance.get(`/webscraper-node/api/overview/in-progress?${params.toString()}`);
};

export const getAllDone = async (
  page: number | string,
  size: number | string,
  sortBy?: string,
  sortOrder?: string,
  search?: string
): Promise<AxiosResponse> => {
  const params = buildOverviewParams(page, size, sortBy, sortOrder, search);
  return axiosAzureInstance.get(`/webscraper-node/api/overview/done?${params.toString()}`);
};

export const getAllFailed = async (
  page: number | string,
  size: number | string,
  sortBy?: string,
  sortOrder?: string,
  search?: string
): Promise<AxiosResponse> => {
  const params = buildOverviewParams(page, size, sortBy, sortOrder, search);
  return axiosAzureInstance.get(`/webscraper-node/api/overview/failed?${params.toString()}`);
};

export const getAllCanceled = async (
  page: number | string,
  size: number | string,
  sortBy?: string,
  sortOrder?: string,
  search?: string
): Promise<AxiosResponse> => {
  const params = buildOverviewParams(page, size, sortBy, sortOrder, search);
  return axiosAzureInstance.get(`/webscraper-node/api/overview/canceled?${params.toString()}`);
};

export const downloadResult = async (id: string, fileName: string): Promise<unknown> => {
  const { data } = await axiosAzureInstance.get(`/webscraper-node/api/download/${id}`, {
    responseType: 'blob',
  });
  FileDownload(data, 'result_' + fileName);
  return data;
};

export const viewFile = async (id: string, fileName: string): Promise<unknown> => {
  const { data } = await axiosAzureInstance.get(`/webscraper-node/api/file/${id}`, {
    responseType: 'blob',
  });
  FileDownload(data, fileName);
  return data;
};

const WebScraperService = {
  scrapeCountry,
  getAvailableCountries,
  getScrapingResults,
  getScrapingStatusTotals,
  getAllInProgress,
  getAllDone,
  getAllFailed,
  getAllCanceled,
  downloadResult,
  viewFile,
  getJobStatus,
  stopJob,
  retryJob,
};

export default WebScraperService;
